<script setup lang="ts">
import { computed, ref } from "vue";

import { IDeleteProgramCommand } from "@/composables/kili/internal/mutations/use-delete-site-program-mutation";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";

import ProgramIcon from "@/components/programming/program-icon.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";
import DropMenu, { IDropMenuItem } from "@/components/ui/menu/drop-menu.vue";

interface IProps {
  canDelete?: boolean;
  isDefaultProgram?: boolean;
  program: SiteProgramOverview;
  siteId: V4HSiteId;
  disabled?: boolean;
}

interface IEmits {
  (e: "select", programId: ProgramId): void;
  (e: "delete", command: IDeleteProgramCommand): void;
  (e: "duplicate", program: SiteProgramOverview): void;
  (e: "clone", programId: ProgramId): void;
}

const props = withDefaults(defineProps<IProps>(), {
  canDelete: true,
  isDefaultProgram: false,
});
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();

const isDeleteProgramConfirmationDialogVisible = ref<boolean>(false);

const dropMenuItems = computed<IDropMenuItem[]>(() => [
  {
    label: "global.misc.details",
    icon: "pi pi-file",
    callback: () => {
      if (props.disabled) return;
      emits("select", props.program.id);
    },
  },
  {
    label: "control.programming.clone.action",
    icon: "pi pi-clone",
    callback: () => {
      if (props.disabled) return;
      emits("clone", props.program.id);
    },
  },
  {
    label: "control.programming.copyToOtherSite.action",
    icon: "pi pi-copy",
    callback: () => {
      if (props.disabled) return;
      emits("duplicate", props.program);
    },
  },
  {
    label: "global.misc.delete",
    icon: "pi pi-trash",
    danger: true,
    callback: () => {
      if (props.disabled) return;
      isDeleteProgramConfirmationDialogVisible.value = true;
    },
    visible: () => props.canDelete,
    withDividerBefore: true,
  },
]);

function handleConfirmDeletion() {
  if (props.disabled) return;

  isDeleteProgramConfirmationDialogVisible.value = false;
  emits("delete", { programId: props.program.id, siteId: props.siteId });
}

function selectProgram() {
  if (props.disabled) return;

  emits("select", props.program.id);
}
</script>

<template>
  <div
    class="bg-white border border-[#F0F0F4] rounded-[12px] p-6 flex items-center gap-2.5 w-full h-[86px] cursor-pointer"
    data-test-id="program-overview-card"
    @click="selectProgram"
  >
    <div
      :aria-label="props.program.name"
      class="flex flex-grow min-w-0 items-center gap-2.5"
      role="button"
      :tabindex="props.disabled ? -1 : 0"
      @keydown.enter="selectProgram"
    >
      <ProgramIcon
        :program-color="props.program.frontDisplayColor"
        :is-site-default="props.isDefaultProgram"
      />

      <div class="flex flex-col min-w-0">
        <h4
          class="text-lg font-semibold truncate text-text-primary leading-normal"
          data-test-id="program-name"
        >
          <template v-if="isDefaultProgram">{{ t(k.control.programming.defaultProgram) }}</template>
          <template v-else>{{ props.program.name }}</template>
        </h4>
      </div>
    </div>

    <DropMenu
      :items="dropMenuItems"
      right
    >
      <HospitalityButton
        data-test-id="program-options-button"
        is-icon-only
        severity="tertiary"
        size="xs"
      >
        <i class="pi pi-ellipsis-h" />
      </HospitalityButton>
    </DropMenu>
  </div>

  <ConfirmationDialog
    v-model="isDeleteProgramConfirmationDialogVisible"
    :callback="() => handleConfirmDeletion()"
    delete-mode
    :subtitle="t(k.control.programming.deletion.subtitle({ name: props.program.name }))"
    :title="t(k.control.programming.deletion.title)"
    :submit-text="t(k.global.misc.deletePermanently)"
  />
</template>
